import type { DateRange, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import {
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  min,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  sub,
} from "date-fns";

import { useTranslation } from "react-i18next";

interface Props {
  /** The starting date. */
  toDate?: Date;
  /** The ending date. */
  fromDate?: Date;

  /** The callback called when the dates change. */
  onChange?(fromDate?: Date, toDate?: Date): void;
}

/**
 * A date range select with shortcuts.
 */
export function DateRangeSelect({ toDate, fromDate, onChange }: Props) {
  const { t } = useTranslation();
  const shortcuts = useShortcuts();

  return (
    <DateRangePicker
      value={[fromDate ?? null, toDate ?? null]}
      onChange={values => onChange?.(values[0] ?? undefined, values[1] ?? undefined)}
      label={t("fromTo")}
      disableFuture
      slots={{
        field: SingleInputDateRangeField,
      }}
      slotProps={{
        field: {
          size: "small",
          fullWidth: true,
        } as never,
        shortcuts: {
          items: shortcuts,
        },
      }}
    />
  );
}

function useShortcuts(): PickersShortcutsItem<DateRange<Date>>[] {
  const now = new Date();
  const { t } = useTranslation();

  function item(label: string, from: Date, to: Date) {
    return {
      label,
      getValue: () => range(from, to),
    };
  }

  function range(from: Date, to: Date): [Date, Date] {
    return [startOfDay(from), min([endOfDay(now), endOfDay(to)])];
  }

  return [
    item(t("today"), now, now),
    item(t("yesterday"), sub(now, { days: 1 }), sub(now, { days: 1 })),
    item(t("thisWeek"), startOfISOWeek(now), endOfISOWeek(now)),
    item(
      t("lastWeek"),
      sub(startOfISOWeek(now), { weeks: 1 }),
      sub(endOfISOWeek(now), { weeks: 1 }),
    ),
    item(t("thisMonth"), startOfMonth(now), endOfMonth(now)),
    item(
      t("lastMonth"),
      startOfMonth(sub(startOfMonth(now), { days: 1 })),
      endOfMonth(sub(startOfMonth(now), { days: 1 })),
    ),
  ];
}
